import type { ComponentPropsWithRef } from 'react';
import { cn } from '@/lib/utils';

export type StatusPillSize = 'xs' | 'sm';

interface StatusPillProps extends ComponentPropsWithRef<'span'> {
  size?: StatusPillSize;
}

// Uppercase glyphs sit above the baseline with no descenders, so with
// `leading-none` they appear top-weighted inside a symmetric line-box.
// We add 1px more top-padding than bottom-padding to visually center them.
const sizeStyles: Record<StatusPillSize, string> = {
  xs: 'text-[9px] px-1.5 pt-[4px] pb-[3px]',
  sm: 'text-[10px] px-1.5 pt-[5px] pb-1',
};

// Spreads `...rest` (and forwards `ref`) so Radix's `TooltipTrigger asChild`
// can attach its pointer/focus handlers and anchor ref directly to the
// rendered span — without this, wrapping a StatusPill in a Tooltip renders
// the pill but the hover/focus triggers never reach the DOM node.
export function StatusPill({ children, className, size = 'sm', ...rest }: StatusPillProps) {
  return (
    <span
      {...rest}
      className={cn(
        'inline-flex items-center justify-center rounded font-semibold leading-none tracking-wide',
        sizeStyles[size],
        className
      )}
    >
      {children}
    </span>
  );
}
